import { Link, useRouterState } from '@tanstack/react-router';
import { Stethoscope, Pill, Dna } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * NotFoundPage — full-bleed standalone 404, rendered OUTSIDE the AppShell
 * by `__root.tsx`. No navbar, no subnav, no destination grid; the page is
 * the page.
 *
 * Decorative element: a slow horizontal marquee of the three product-type
 * icons (device, drug, biologic) drifts behind the 404 so the page feels
 * alive without breaking the regulatory tone. The marquee is duplicated
 * head-to-tail and translated by -50% over the loop so it never seams.
 */
export function NotFoundPage() {
  const path = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="min-h-screen w-full bg-bg flex flex-col items-center justify-center px-6 py-16 overflow-hidden">
      <ProductTypeMarquee />

      <div className="w-full max-w-[520px] text-center relative z-10">
        <img
          src="/regara_logo_1.png"
          alt="REGARA"
          className="h-6 w-auto mx-auto mb-7 block opacity-90"
        />

        <span className="inline-block text-electric font-bold uppercase tracking-[0.22em] text-[11.5px] mb-4">
          Error 404
        </span>

        <h1
          className="text-navy m-0 mb-3 leading-[1.15]"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: 'clamp(32px, 4.2vw, 42px)',
          }}
        >
          We couldn&rsquo;t find that page.
        </h1>

        <p className="text-muted text-[15px] m-0 mb-2 leading-relaxed">
          The link may be out of date, or the page doesn&rsquo;t exist.
        </p>
        <p className="text-muted text-[13px] m-0 mb-8">
          <code className="font-mono text-[12.5px] bg-line-soft text-navy rounded px-1.5 py-0.5">
            {path}
          </code>
        </p>

        <Button asChild variant="brand">
          <Link to="/dashboard">Return to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * ProductTypeMarquee — three-icon strip drifting horizontally behind the
 * content. Duplicated twice (head + tail) so the linear translateX(-50%)
 * animation loops seamlessly. Fades to transparent at both edges via a
 * mask-image gradient so icons aren't cut off mid-glyph.
 */
function ProductTypeMarquee() {
  const ICONS = [
    { Icon: Stethoscope, label: 'Medical Device' },
    { Icon: Pill, label: 'Drug' },
    { Icon: Dna, label: 'Biologic' },
  ];
  const STRIP = [...ICONS, ...ICONS, ...ICONS, ...ICONS, ...ICONS, ...ICONS];

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-[140px] flex items-center"
      style={{
        WebkitMaskImage:
          'linear-gradient(90deg, transparent 0%, black 18%, black 82%, transparent 100%)',
        maskImage:
          'linear-gradient(90deg, transparent 0%, black 18%, black 82%, transparent 100%)',
      }}
    >
      <div
        className="flex items-center gap-20 whitespace-nowrap will-change-transform"
        style={{ animation: 'var(--animate-marquee-x)' }}
      >
        {STRIP.concat(STRIP).map(({ Icon, label }, i) => (
          <Icon
            key={`${label}-${i}`}
            className="size-16 text-navy/8 shrink-0"
            strokeWidth={1.5}
          />
        ))}
      </div>
    </div>
  );
}
